import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Checkbox from "../../../../../components/Form/Checkbox";
import Button, { Color } from "../../../../../components/Form/Button";
import Lottie from "lottie-react";

import forkliftAnim from "../../../../../assets/img/animations/forklift.json";
import moneyAnim from "../../../../../assets/img/animations/money.json";
import truckAnim from "../../../../../assets/img/animations/truck.json";

import { cardSx, labelSx, backButtonSx, nextButtonSx } from "./styles";

export type ThirdStepForm = { areas?: string[] };

interface ThirdStepProps {
  isLoading?: boolean;
  objectPreview?: ThirdStepForm;
  handleNextStep?: () => void;
  onPreviousStep?: () => void;
}

export interface ThirdStepHandle {
  areas: string[];
}

type Area = { id: string; labelKey: string; anim: any };

const ThirdStep: ForwardRefRenderFunction<ThirdStepHandle, ThirdStepProps> = (
  { isLoading, objectPreview, handleNextStep, onPreviousStep },
  ref
) => {
  const { t: translate } = useTranslation();

  const AREAS: Area[] = useMemo(
    () => [
      { id: "sales", labelKey: "signup.right.sales", anim: moneyAnim },
      { id: "operations", labelKey: "signup.right.operations", anim: truckAnim },
      { id: "warehousing", labelKey: "signup.right.warehousing", anim: forkliftAnim },
    ],
    []
  );

  const [selected, setSelected] = useState<string[]>(objectPreview?.areas ?? []);
  useImperativeHandle(ref, () => ({ areas: selected }));

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const isSelected = (id: string) => selected.includes(id);

  const Card = ({ area }: { area: Area }) => (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => toggle(area.id)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle(area.id)}
      sx={cardSx(isSelected(area.id))}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Checkbox
            checked={isSelected(area.id)}
            control={{ "data-cy": `area-${area.id}` }}
            disabled={isLoading}
          />
          <Typography sx={labelSx(isSelected(area.id))}>
            {translate(area.labelKey)}
          </Typography>
        </Stack>

        <Box sx={{ width: 52, height: 52, mr: 0.5 }}>
          <Lottie animationData={area.anim} loop autoplay style={{ width: "100%", height: "100%" }} />
        </Box>
      </Stack>
    </Box>
  );

  return (
    <>
      <Grid item xs={12} sx={{ mb: "24px" }}>
        <Stack spacing={2}>
          {AREAS.map((a) => (
            <Card key={a.id} area={a} />
          ))}
        </Stack>
      </Grid>

      <Grid item xs={12} container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onPreviousStep}
            disabled={isLoading}
            data-cy="backButton"
            aria-label="backButton"
            sx={backButtonSx}
          >
            {translate("signup.right.back")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            customColor={Color.SECONDARY_DARK}
            onClick={handleNextStep}
            disabled={selected.length === 0 || !!isLoading}
            data-cy="nextStepButton"
            sx={nextButtonSx}
          >
            {translate("signup.right.next")}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default forwardRef(ThirdStep);
